import { ArdoqError, isArdoqError, sleep } from '@ardoq/common-helpers';
import { HTTPStatusCode } from '@ardoq/api-types';
import { ApiResponse } from './types';
import { getErrorStatusCode } from './error';

// MAX_ATTEMPTS bounds the *total* number of attempts, including the initial
// one — e.g. 3 means 1 initial attempt + up to 2 retries.
const MAX_ATTEMPTS = 3;
const INITIAL_DELAY_MS = 500;

// 408 (Request Timeout) and 429 (Too Many Requests) are 4xx codes but are
// transient by definition, so they back off like 5xx rather than failing
// immediately — a 429 in particular is exactly what backoff exists for.
const RETRYABLE_CLIENT_ERRORS: ReadonlySet<number> = new Set([
  HTTPStatusCode.REQUEST_TIMEOUT,
  HTTPStatusCode.TOO_MANY_REQUESTS,
]);

// Non-HTTP failures (status 0, e.g. network down) and 5xx are transient.
// Within the 4xx range only the codes above are retried; every other 4xx is a
// terminal client error.
const isRetryableError = (error: ArdoqError): boolean => {
  const statusCode = getErrorStatusCode(error);
  if (statusCode >= 400 && statusCode <= 499) {
    return RETRYABLE_CLIENT_ERRORS.has(statusCode);
  }
  return true;
};

const backoffDelayMs = (attempt: number): number =>
  INITIAL_DELAY_MS * 2 ** (attempt - 1);

const requestWithRetries = async <T>(
  request: () => ApiResponse<T>,
  attempt: number
): ApiResponse<T> => {
  const result = await request();
  if (!isArdoqError(result)) return result;
  if (!isRetryableError(result) || attempt >= MAX_ATTEMPTS) return result;
  await sleep(backoffDelayMs(attempt));
  return requestWithRetries(request, attempt + 1);
};

/**
 * Performs an API request, retrying transient failures — network errors, 5xx,
 * and the transient 408/429 client errors — with exponential backoff (500ms,
 * then 1000ms; 3 attempts in total). Other client (4xx) errors are terminal
 * and resolve immediately. Like every api-layer request, the returned promise
 * resolves to a `Result<T>` — the last `ArdoqError` on failure — and never
 * rejects.
 */
export const retriableApiRequest = <T>(
  request: () => ApiResponse<T>
): ApiResponse<T> => requestWithRetries(request, 1);
